import {type Dispatch, useState} from 'react';
import {Link} from 'react-router-dom';
import type {Track} from '../../shared/api/useMusic';
import type {BallotResource, VotingSystem} from '../../shared/ballotTypes';
import {ArrowLeftIcon} from '../../shared/components/icons';
import {PaperButton, Segmented, StickerCard, Toggle} from '../../shared/sticker/stickers';
import {MusicPicker} from './components/musicPicker';
import {OptionList} from './components/optionList';
import {ResultsPreviewCard} from './components/resultsPreviewCard';
import {ResultsSettings, type ResultsValue} from './components/resultsSettings';
import {SlackImportContainer} from './components/slackImportContainer';
import {formatDuration, parseDuration} from './duration';
import type {EditorAction, EditorDraft} from './editorState';

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

const SYSTEMS: {value: VotingSystem; label: string; hint: string}[] = [
    {value: 'fptp', label: 'First past the post', hint: 'One vote each'},
    {value: 'preferential', label: 'Preferential', hint: 'Rank them, runoff rounds'},
    {value: 'dots', label: 'Dot voting', hint: 'Spread your dots'},
    {value: 'approval', label: 'Approval', hint: 'Tick all you like'},
];
const PRESETS = [
    {label: '1m', seconds: 60},
    {label: '2m', seconds: 120},
    {label: '5m', seconds: 300},
];
const SAVE_LABEL: Record<SaveState, string> = {
    idle: '',
    saving: 'Saving...',
    saved: 'Saved',
    failed: 'Could not save. Retrying',
};

const Timer = ({
    seconds,
    disabled,
    onChange,
}: {
    seconds: number;
    disabled: boolean;
    onChange: (seconds: number) => void;
}) => {
    const [text, setText] = useState<string | null>(null);
    const commit = () => {
        const parsed = text === null ? null : parseDuration(text);
        if (parsed !== null) onChange(parsed);
        setText(null);
    };
    return (
        <div className="bt-row">
            <input
                className="bt-field bt-field--time"
                aria-label="Timer"
                value={text ?? formatDuration(seconds)}
                disabled={disabled}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setText(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            />
            {PRESETS.map((p) => (
                <PaperButton
                    key={p.label}
                    variant="ghost"
                    className="bt-button--small"
                    disabled={disabled}
                    onClick={() => onChange(p.seconds)}
                >
                    {p.label}
                </PaperButton>
            ))}
        </div>
    );
};

const DotsField = ({
    value,
    disabled,
    onChange,
}: {
    value: number;
    disabled: boolean;
    onChange: (value: number) => void;
}) => {
    const [text, setText] = useState<string | null>(null);
    const commit = () => {
        const parsed = Number.parseInt(text ?? '', 10);
        if (!Number.isNaN(parsed)) onChange(Math.min(20, Math.max(1, parsed)));
        setText(null);
    };
    return (
        <input
            id="dots-per-voter"
            className="bt-field bt-field--dots"
            type="number"
            min={1}
            max={20}
            value={text ?? value}
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
    );
};

export type EditorProps = {
    ballot: BallotResource;
    draft: EditorDraft;
    dispatch: Dispatch<EditorAction>;
    results: ResultsValue;
    onResults: (next: ResultsValue) => void;
    tracks: Track[];
    saveState: SaveState;
    slackAvailable: boolean;
    onPresent: () => void;
};

export const Editor = ({
    ballot,
    draft,
    dispatch,
    results,
    onResults,
    tracks,
    saveState,
    slackAvailable,
    onPresent,
}: EditorProps) => {
    const locked = ballot.status !== 'draft';
    const [importing, setImporting] = useState(false);

    return (
        <div className="bt-editor">
            <div className="bt-editor__bar">
                <Link to="/admin" className="bt-editor__back">
                    <ArrowLeftIcon /> Your votes
                </Link>
                <span className="bt-editor__saved" role="status">
                    {SAVE_LABEL[saveState]}
                </span>
                <PaperButton onClick={onPresent}>Open presenter</PaperButton>
            </div>
            {locked && (
                <StickerCard seed="locked-note" className="bt-note">
                    Voting has started, so this vote is locked.
                </StickerCard>
            )}
            <div className="bt-editor__cols">
                <section className="bt-panel bt-editor__left">
                    <label className="bt-label" htmlFor="ballot-title">
                        Title
                    </label>
                    <input
                        id="ballot-title"
                        className="bt-field bt-field--title"
                        maxLength={120}
                        value={draft.title}
                        disabled={locked}
                        onChange={(e) =>
                            dispatch({type: 'set', field: 'title', value: e.target.value})
                        }
                    />
                    <label className="bt-label" htmlFor="ballot-description">
                        Description (optional)
                    </label>
                    <input
                        id="ballot-description"
                        className="bt-field"
                        maxLength={400}
                        value={draft.description}
                        disabled={locked}
                        onChange={(e) =>
                            dispatch({type: 'set', field: 'description', value: e.target.value})
                        }
                    />
                    <hr className="bt-divider" />
                    <div className="bt-row">
                        <div className="bt-label">Options &middot; {draft.options.length}</div>
                        <span className="bt-row__push" />
                        {slackAvailable && (
                            <PaperButton
                                variant="ghost"
                                className="bt-button--small"
                                disabled={locked}
                                onClick={() => setImporting(true)}
                            >
                                Import from Slack
                            </PaperButton>
                        )}
                        <PaperButton
                            variant="ghost"
                            className="bt-button--small"
                            disabled={locked}
                            onClick={() => dispatch({type: 'addOption', id: crypto.randomUUID()})}
                        >
                            + Add option
                        </PaperButton>
                    </div>
                    <OptionList options={draft.options} disabled={locked} dispatch={dispatch} />
                    {importing && (
                        <SlackImportContainer
                            existing={draft.options.length}
                            dispatch={dispatch}
                            onClose={() => setImporting(false)}
                        />
                    )}
                    {draft.options.length > 1 && <div className="bt-help">Drag to reorder.</div>}
                </section>

                <section className="bt-panel bt-editor__right">
                    <div className="bt-label">Voting system</div>
                    <Segmented
                        name="voting-system"
                        columns={2}
                        options={SYSTEMS}
                        value={draft.system}
                        disabled={locked}
                        onChange={(value) => dispatch({type: 'set', field: 'system', value})}
                    />
                    {draft.system === 'dots' && (
                        <div className="bt-row">
                            <label htmlFor="dots-per-voter">Dots per voter</label>
                            <DotsField
                                value={draft.dotsPerVoter}
                                disabled={locked}
                                onChange={(value) =>
                                    dispatch({type: 'set', field: 'dotsPerVoter', value})
                                }
                            />
                        </div>
                    )}
                    <hr className="bt-divider" />
                    <ResultsSettings value={results} disabled={locked} onChange={onResults} />
                    <ResultsPreviewCard value={results} options={draft.options} />
                    <hr className="bt-divider" />
                    <div className="bt-label">Timer</div>
                    <Timer
                        seconds={draft.durationSeconds}
                        disabled={locked}
                        onChange={(value) =>
                            dispatch({type: 'set', field: 'durationSeconds', value})
                        }
                    />
                    <div className="bt-label">Music</div>
                    <MusicPicker
                        tracks={tracks}
                        value={draft.musicTrackId}
                        disabled={locked}
                        onChange={(value) => dispatch({type: 'set', field: 'musicTrackId', value})}
                    />
                    <Toggle
                        label="Block self-votes"
                        hint={'Shows "NICE TRY" if someone picks themselves'}
                        checked={draft.blockSelfVotes}
                        disabled={locked}
                        onChange={(value) =>
                            dispatch({type: 'set', field: 'blockSelfVotes', value})
                        }
                    />
                </section>
            </div>
        </div>
    );
};
